import { Layout, Menu, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useModuleSidebar } from './sidebars/useModuleSidebar';
import type { SidebarConfig } from './sidebars/types';

const { Sider } = Layout;
const { Text } = Typography;

interface ModuleSidebarProps {
  collapsed: boolean;
}

/**
 * Renders the active module's deep sub-nav.
 *
 * If no module config matches the current URL (e.g. on `/`, `/about`,
 * `/dashboard`), the sider collapses to width 0 — keeping the primary rail
 * clean and the content area wider.
 *
 * Section headings come from the same i18n keys the page bodies use, so the
 * sidebar copy stays in sync when those strings are tuned.
 */
export default function ModuleSidebar({ collapsed }: ModuleSidebarProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const config = useModuleSidebar();

  if (!config) {
    // No deep sidebar for this route — collapse to 0 width.
    return (
      <Sider
        width={0}
        collapsedWidth={0}
        trigger={null}
        style={{ flex: '0 0 0' }}
        className="ga-module-sidebar"
      />
    );
  }

  const items = buildMenuItems(config, t);
  const selectedKeys = computeSelectedKeys(config, location.pathname);
  const openKeys = config.sections.map((s) => `section:${s.key}`);

  return (
    <Sider
      collapsed={collapsed}
      collapsedWidth={0}
      width={240}
      theme="light"
      trigger={null}
      className="ga-module-sidebar"
      style={{
        borderInlineEnd: '1px solid rgba(5, 5, 5, 0.06)',
        background: '#fff',
      }}
    >
      <div style={{ padding: '16px 16px 8px' }}>
        <Text strong style={{ fontSize: 14, color: 'rgba(0,0,0,0.88)' }}>
          <span style={{ marginInlineEnd: 8 }}>{config.icon}</span>
          {t(config.titleKey)}
        </Text>
      </div>
      <Menu
        mode="inline"
        selectedKeys={selectedKeys}
        defaultOpenKeys={openKeys}
        onClick={(e) => {
          // Item keys are absolute paths (see buildMenuItems).
          navigate(e.key);
        }}
        items={items}
        style={{ borderInlineEnd: 'none', background: 'transparent' }}
      />
    </Sider>
  );
}

function buildMenuItems(config: SidebarConfig, t: (k: string) => string) {
  return config.sections.map((section) => ({
    key: `section:${section.key}`,
    type: 'group' as const,
    label: (
      <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.6, textTransform: 'uppercase' }}>
        {t(section.labelKey)}
      </span>
    ),
    children: section.items.map((item) => ({
      key: item.path,
      icon: item.icon,
      label: t(item.labelKey),
    })),
  }));
}

/**
 * Pick the most-specific sub-item path that prefixes the current pathname.
 * Falls back to the module root if nothing matches (e.g. on the module
 * landing page that hasn't redirected yet).
 */
function computeSelectedKeys(config: SidebarConfig, pathname: string): string[] {
  const allPaths = config.sections.flatMap((s) => s.items.map((i) => i.path));
  const match = allPaths
    .filter((p) => pathname === p || pathname.startsWith(p + '/'))
    .sort((a, b) => b.length - a.length)[0];
  return match ? [match] : [];
}
